import React, { useEffect, useRef, useState } from 'react';
import { Button } from "antd";
import { debounce } from "lodash";
import { usePostHog } from 'posthog-js/react';
import { detectDevice } from '@/utils'
import { Dialog } from 'antd-mobile'
import { MAC_DOWNLOAD, WIN_DOWNLOAD } from '@/config/constent';
import './styles.scss';
import { RenderFooter } from '@/pages/RenderFooter';

interface ScrollLazyLoadImageProps {
  className?: string;
  style?: React.CSSProperties;
  date?: string;
  hasFooter?: boolean;
  total?: number;
  pictureExt?: string;
}

// 在TypeScript中，我们需要声明require函数的类型
declare function require(moduleName: string): any;

// 获取图片路径数组
const getImagePaths = (total: number, pictureExt: string) => {
  const imageNames: string[] = [];
  // 长图切片文件夹中共有16张图片(0000-0015)
  for (let i = 0; i <= total; i++) {
    // 格式化数字，确保是4位数字（如0001）
    // const index = i.toString().padStart(4, '0');
    // 新的文件命名格式为：长图_0000.png, 长图_0001.png等
    imageNames.push(`cemeta_img${i + 1}.${pictureExt}`);
  }
  return imageNames;
};

const ScrollLazyLoadImage: React.FC<ScrollLazyLoadImageProps> = ({ className, style, date = '20250902', pictureExt = 'webp', hasFooter = true, total = 14 }) => {
  const posthog = usePostHog();
  const isPc = detectDevice()

  const searchParamsString = window.location.href.split('?')[1] || '';
  const searchParams = new URLSearchParams(searchParamsString);
  const source = searchParams.get('s'); // source 参数值
  const detail = searchParams.get('d'); // detail 参数值

  const [visibleImages, setVisibleImages] = useState<Set<number>>(new Set());
  const [loadedImages, setLoadedImages] = useState<Map<number, string>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const imageNames = getImagePaths(total, pictureExt);

  // const MAC_DOWNLOAD =
  // "https://haohui-zips-1313090634.cos.ap-beijing.myqcloud.com/CeMetaHAOHUIV1.3.0%28MacOS%29.zip";
  // const WIN_DOWNLOAD =
  //   "https://haohui-zips-1313090634.cos.ap-beijing.myqcloud.com/CeMetaHAOHUIV1.3.0%28Windows%29.zip";
  const TEACH_URL =
    "https://mi8elatzm1x.feishu.cn/wiki/SfaxwB67JiEoHAkvpf1cVUfInVf?from=from_copylink";
  const macRef = useRef<HTMLAnchorElement | null>(null);
  const winRef = useRef<HTMLAnchorElement | null>(null);

  const handleMACDownload = debounce(() => {
    if (!isPc) {
      Dialog.alert({
        content: '请在电脑端打开！',
        onConfirm: () => {
          console.log('Confirmed')
        },
      })
      return
    }
    if (macRef.current) {
      macRef.current?.click();
      if (source && detail) {
        posthog?.capture('haohui_source_download', {
          source: source,
          detail: detail,
          type: 'mac'
        });
      }
      posthog?.capture('haohui_download', {
        type: 'mac',
        info: navigator?.userAgent
      });
    }
  }, 1000);

  const handleWINDownload = debounce(() => {
    if (!isPc) {
      Dialog.alert({
        content: '请在电脑端打开！',
        onConfirm: () => {
          console.log('Confirmed')
        },
      })
      return
    }
    if (winRef.current) {
      winRef.current?.click();
      if (source && detail) {
        posthog?.capture('haohui_source_download', {
          source: source,
          detail: detail,
          type: 'win'
        });
      }
      posthog?.capture('haohui_download', {
        type: 'win',
        info: navigator?.userAgent
      });
    }
  }, 1000);

  // 懒加载图片
  const lazyLoadImage = (index: number) => {
    try {
      // 动态导入图片
      // const imagePath = require(`../../cemeta_img/${imageNames[index]}`);
      const imageUrl = `https://ps-cdn.cemeta.cn/configs/frontend/${date}/cemeta_img/cemeta_img${index + 1}.${pictureExt}`;
      // const imageUrl = imagePath.default || imagePath;
      
      setLoadedImages((prev) => new Map(prev).set(index, imageUrl));
    } catch (error) {
      console.error(`Failed to load image at index ${index}:`, error);
    }
  };

  useEffect(() => {
    // 设置 Intersection Observer 来监测图片占位符是否进入视口
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            
            // 标记为可见并开始加载
            setVisibleImages((prev) => new Set(prev).add(index));
            lazyLoadImage(index);
            
            // 一旦加载，就不再监测该占位符
            if (observerRef.current) {
              observerRef.current.unobserve(entry.target);
            }
          }
        });
      },
      {
        rootMargin: '200px 0px', // 提前200px开始加载
        threshold: 0.01, // 只要有1%可见就触发
      }
    );

    // 开始监测所有图片占位符
    if (containerRef.current) {
      const placeholders = containerRef.current.querySelectorAll('.image-placeholder');
      placeholders.forEach((placeholder) => {
        if (observerRef.current) {
          observerRef.current.observe(placeholder);
        }
      });
    }

    // 清理函数
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`scroll-lazy-load-image ${className || ''}`}
      style={isPc ? style : { ...style, paddingBottom: '4em' }}
    >
      {imageNames.map((name, index) => {
        const imageUrl = loadedImages.get(index);
        const isVisible = visibleImages.has(index);
        
        return (
          <div 
            key={index} 
            className={isPc ? "image-container" : "mobile-image-container"}
          >
            {/* 图片占位符 */}
            <div 
              ref={(el) => {
                imageRefs.current[index] = el;
              }}
              className="image-placeholder"
              data-index={index}
              style={{
                width: '100%',
                // minHeight: '200px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'transparent',
              }}
            >
              {isVisible && imageUrl ? (
                <div style={{ position:'relative' }}>
                  <img
                    src={imageUrl}
                    style={{width: '100%', display: 'block' }}
                    alt=""
                    aria-hidden="true"
                    className="lazy-image"
                    loading="lazy"
                  />
                  {
                    index === 9 && isPc
                      ? (
                        <div className="homeBox-footer-button" style={{ position: 'absolute', width: '100%', justifyContent: 'center', bottom: '10px', background: 'transparent' }}>
                          <Button
                            style={isPc ? { marginRight: "32px" } : { marginLeft: "2em", marginRight: "32px" }}
                            onClick={handleMACDownload}
                          >
                            <img
                              style={{ width: "26px", height: "26px", marginRight: "4px" }}
                              alt=""
                              aria-hidden="true"
                              src="/logo/svg/mac.svg"
                            />
                            mac 版本下载
                          </Button>
                          <Button
                            style={{ marginRight: "32px" }}
                            onClick={handleWINDownload}
                          >
                            <img
                              style={{ width: "26px", height: "26px", marginRight: "4px" }}
                              alt=""
                              aria-hidden="true"
                              src="/logo/svg/windows.svg"
                            />
                            win 版本下载
                          </Button>
                          {/* <Button onClick={handleTeach}>
                            <img
                              style={{ width: "26px", height: "26px", marginRight: "4px" }}
                              alt="teach.svg"
                              src="/logo/svg/teach.svg"
                            />
                            使用教程
                          </Button> */}
                            <a
                              ref={macRef}
                              href={MAC_DOWNLOAD}
                              style={{ visibility: "hidden" }}
                            >
                              {" "}
                            </a>
                            <a
                              ref={winRef}
                              href={WIN_DOWNLOAD}
                              style={{ visibility: "hidden" }}
                            >
                              {" "}
                            </a>
                        </div>
                      )
                      : null
                  }
                </div>
               
              ) : isVisible ? (
                // 加载状态提示
                <div className="loading-skeleton"></div>
              ) : null
                }
            </div>
          </div>
        );
      })}
      {hasFooter ? <RenderFooter /> : null}
    </div>
  );
};

export default ScrollLazyLoadImage;